import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
// Selector for the elements the focus trap cycles through.
const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * Right-side drawer (slide-over) overlay — a design-system primitive (design.md
 * §5 / §8 / §10). Dims the page behind a backdrop, traps keyboard focus while
 * open, locks body scroll, and closes via the ✕ button, Escape, or a backdrop
 * click. Focus returns to whatever was focused when it opened (the trigger).
 *
 * Rendered into a portal and only while `open` — never a pre-rendered DOM
 * element (design.md §8).
 */
export function Drawer({ open, onClose, title, children }) {
    const panelRef = useRef(null);
    const titleId = useId();
    // While open: lock scroll, move focus in, restore focus on close, and handle
    // Escape + a Tab focus trap. All teardown happens in one cleanup so close —
    // whether by prop flip or unmount — always restores the page.
    useEffect(() => {
        if (!open)
            return;
        const previouslyFocused = document.activeElement;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // Focus the panel itself so focus is "inside the drawer" even before any
        // focusable content exists (e.g. during the loading skeleton).
        panelRef.current?.focus();
        function onKeyDown(event) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key !== 'Tab')
                return;
            const panel = panelRef.current;
            if (!panel)
                return;
            const focusable = Array.from(panel.querySelectorAll(FOCUSABLE));
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) {
                // Nothing tabbable yet — keep focus pinned to the panel.
                event.preventDefault();
                panel.focus();
                return;
            }
            const active = document.activeElement;
            if (event.shiftKey && (active === first || active === panel)) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
            previouslyFocused?.focus?.();
        };
    }, [open, onClose]);
    if (!open)
        return null;
    return createPortal(_jsxs("div", { className: "fixed inset-0 z-50 flex justify-end", children: [_jsx("div", { className: "absolute inset-0 bg-[rgba(25,28,29,0.4)] animate-overlay-in", "aria-hidden": "true", onClick: onClose }), _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, tabIndex: -1, className: "relative h-full w-full max-w-[640px] flex flex-col bg-surface shadow-[0_4px_16px_rgba(25,28,29,0.06)] outline-none animate-drawer-in", children: [_jsxs("div", { className: "flex items-start justify-between gap-4 p-6 border-b border-outline-variant", children: [_jsx("div", { id: titleId, className: "min-w-0", children: title }), _jsx("button", { type: "button", onClick: onClose, "aria-label": "Close", className: "shrink-0 -m-1 p-1 rounded-[8px] text-on-surface-variant hover:text-on-surface hover:bg-surface-container cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M5 5l10 10M15 5L5 15", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" }) }) })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-6", children: children })] })] }), document.body);
}
